/**
 * Support Dashboard Page (Admin)
 *
 * Admin dashboard for support team showing KPIs, urgent tickets, and queue overview.
 * Provides quick access to ticket management and analytics.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MessageSquare,
  Clock,
  Star,
  CheckCircle2,
  AlertCircle,
  TrendingUp,
  Users,
} from 'lucide-react';
import { PageLayout } from '../../components/layouts/PageLayout';
import { Card, CardHeader, CardBody } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { EmptyState } from '../../components/common/EmptyState';
import { TicketCard } from '../../components/support/TicketCard';
import type { SupportTicket, SupportStats } from '../../types/support';

export const SupportDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<SupportStats | null>(null);
  const [urgentTickets, setUrgentTickets] = useState<SupportTicket[]>([]);
  const [unassignedTickets, setUnassignedTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Import mock API and data
      const { mockApi } = await import('../../services/mockApi');
      const { mockTickets } = await import('../../data/support');

      // Get stats
      const statsResponse = await mockApi.getSupportStatsApi();
      if (statsResponse.success) {
        setStats(statsResponse.data || null);
      }

      // Get urgent tickets (priority = urgent, status = open or in_progress)
      const urgent = mockTickets.filter(
        (t) =>
          t.priority === 'urgent' &&
          (t.status === 'open' || t.status === 'in_progress' || t.status === 'waiting_internal')
      );
      setUrgentTickets(urgent);

      // Get unassigned tickets (assignedTo is undefined/null, status = open)
      const unassigned = mockTickets.filter(
        (t) => !t.assignedTo && (t.status === 'open' || t.status === 'in_progress')
      );
      setUnassignedTickets(unassigned);
    } catch (error) {
      console.error('Failed to load support dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageLayout
      pageTitle="Support Dashboard"
      breadcrumbs={[
        { label: 'Home', href: '/' },
        { label: 'Admin', href: '/admin/dashboard' },
        { label: 'Support' },
      ]}
      actions={
        <Button variant="primary" onClick={() => navigate('/admin/support/tickets')}>
          View All Tickets
        </Button>
      }
    >
      {/* KPI Cards */}
      {stats && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardBody>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-neutral-600">Open Tickets</p>
                    <p className="text-3xl font-bold text-warning-600">{stats.openTickets}</p>
                    <p className="text-xs text-neutral-500 mt-1">
                      {stats.inProgressTickets} in progress
                    </p>
                  </div>
                  <MessageSquare className="w-8 h-8 text-warning-600" />
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-neutral-600">Avg Response Time</p>
                    <p className="text-3xl font-bold text-info-600">
                      {stats.avgResponseTime.toFixed(1)}
                      <span className="text-base font-normal ml-1">hrs</span>
                    </p>
                    <p className="text-xs text-success-600 mt-1 flex items-center gap-1">
                      <TrendingUp className="w-3 h-3" />
                      15% faster than last week
                    </p>
                  </div>
                  <Clock className="w-8 h-8 text-info-600" />
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-neutral-600">Satisfaction Rating</p>
                    <p className="text-3xl font-bold text-success-600">
                      {stats.satisfactionRating.toFixed(1)}
                      <span className="text-base font-normal ml-1">/5</span>
                    </p>
                    <p className="text-xs text-neutral-500 mt-1">Based on {stats.totalTickets} ratings</p>
                  </div>
                  <Star className="w-8 h-8 text-success-600 fill-success-600" />
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-neutral-600">Resolved Today</p>
                    <p className="text-3xl font-bold text-primary-600">{stats.resolvedToday}</p>
                    <p className="text-xs text-neutral-500 mt-1">
                      {stats.totalTickets} total tickets
                    </p>
                  </div>
                  <CheckCircle2 className="w-8 h-8 text-primary-600" />
                </div>
              </CardBody>
            </Card>
          </div>

          {/* Additional Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <Card>
              <CardBody>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-gray-900">Avg Resolution Time</h3>
                  <Clock className="w-5 h-5 text-neutral-600" />
                </div>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.avgResolutionTime.toFixed(1)}
                  <span className="text-base font-normal ml-1">hours</span>
                </p>
                <div className="mt-2 h-2 bg-neutral-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary-600 rounded-full"
                    style={{ width: `${Math.min((stats.avgResolutionTime / 24) * 100, 100)}%` }}
                  />
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-gray-900">Active Support Staff</h3>
                  <Users className="w-5 h-5 text-neutral-600" />
                </div>
                <p className="text-2xl font-bold text-gray-900">
                  8<span className="text-base font-normal ml-1">online</span>
                </p>
                <p className="text-sm text-neutral-600 mt-2">12 total team members</p>
              </CardBody>
            </Card>
          </div>
        </>
      )}

      {/* Urgent Tickets Queue */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-error-600" />
              <h3 className="text-lg font-semibold">Urgent Tickets</h3>
              {urgentTickets.length > 0 && (
                <span className="px-2 py-0.5 bg-error-100 text-error-700 rounded-full text-xs font-medium">
                  {urgentTickets.length}
                </span>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/admin/support/tickets?priority=urgent')}
            >
              View All
            </Button>
          </div>
        </CardHeader>
        <CardBody>
          {loading ? (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-28 bg-neutral-200 rounded-lg"></div>
                </div>
              ))}
            </div>
          ) : urgentTickets.length === 0 ? (
            <EmptyState
              icon={<CheckCircle2 className="w-10 h-10" />}
              title="No Urgent Tickets"
              description="All urgent tickets have been addressed."
            />
          ) : (
            <div className="space-y-3">
              {urgentTickets.map((ticket) => (
                <TicketCard
                  key={ticket.id}
                  ticket={ticket}
                  showCompany
                  onClick={() => navigate(`/admin/support/tickets/${ticket.id}`)}
                />
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Unassigned Tickets */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-warning-600" />
              <h3 className="text-lg font-semibold">Unassigned Tickets</h3>
              {unassignedTickets.length > 0 && (
                <span className="px-2 py-0.5 bg-warning-100 text-warning-700 rounded-full text-xs font-medium">
                  {unassignedTickets.length}
                </span>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/admin/support/tickets?assignedTo=unassigned')}
            >
              View All
            </Button>
          </div>
        </CardHeader>
        <CardBody>
          {loading ? (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-28 bg-neutral-200 rounded-lg"></div>
                </div>
              ))}
            </div>
          ) : unassignedTickets.length === 0 ? (
            <EmptyState
              icon={<CheckCircle2 className="w-10 h-10" />}
              title="No Unassigned Tickets"
              description="All tickets have been assigned to support staff."
            />
          ) : (
            <div className="space-y-3">
              {unassignedTickets.map((ticket) => (
                <TicketCard
                  key={ticket.id}
                  ticket={ticket}
                  showCompany
                  onClick={() => navigate(`/admin/support/tickets/${ticket.id}`)}
                />
              ))}
            </div>
          )}
        </CardBody>
      </Card>
    </PageLayout>
  );
};
